import { useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
} from "@mui/material";
import classNames from "classnames";
import { GoAlert } from "react-icons/go";
import { Link } from "react-router-dom";

import { ECourseStatus } from "../../../../../@types";
import { useAccount } from "../../../../../hooks/useAccount";
import { useStripe } from "../../../../../hooks/useStripe";
import { useMyCourseInstructor } from "../../hooks/useMyCourseInstructor";

export function MyCoursePublishing() {
	const { account } = useAccount();
	const { stripeData } = useStripe();
	const { course, lessons, courseDocuments, updateCourseStatus } =
		useMyCourseInstructor();
	const [confirmUnpublishDialogOpen, setConfirmUnpublishDialogOpen] =
		useState(false);
	if (!course) {
		return null;
	}

	const isFinishedStripe =
		!account?.requiredKYC && stripeData?.status === "ACTIVE";

	const warnings = [];
	if (course.description.trim() === "") {
		warnings.push("Fill service description.");
	}

	if (courseDocuments.length === 0) {
		warnings.push("Upload at least 1 picture/video for your service.");
	}

	if (lessons.length === 0) {
		warnings.push("Create at least 1 class");
	}

	for (let i = 0; i < lessons.length; i++) {
		if (lessons[i].description.trim() === "") {
			warnings.push(`Fill description for class ${lessons[i].title}`);
		}

		if (Number(lessons[i].teachingTime) + Number(lessons[i].flightTime) === 0) {
			warnings.push(
				`Fill teaching time and flight time for class ${lessons[i].title}`,
			);
		}
	}

	const onClickPublishCourse = () => {
		updateCourseStatus(ECourseStatus.FLYING);
	};

	const onClickUnpublishCourse = () => {
		onClickCloseConfirmUnpublishDialog();
		updateCourseStatus(ECourseStatus.GROUNDED);
	};

	const onClickOpenConfirmUnpublishDialog = () => {
		setConfirmUnpublishDialogOpen(true);
	};

	const onClickCloseConfirmUnpublishDialog = () => {
		setConfirmUnpublishDialogOpen(false);
	};

	return (
		<div className="my-course-basic-info-container">
			<Typography className="small-title-text bold">Publishing</Typography>
			{course.status === ECourseStatus.FLYING && (
				<>
					<Typography className="normal-text item-separator-small">
						Your service appears in search results -{" "}
						<Link to={`/services/${course.courseSlug}`} className="normal-link">
							Check it out!
						</Link>
					</Typography>
					<Typography className="normal-text item-separator-small">
						Review the learning experience -{" "}
						<Link
							to={`/my-services/${course.courseSlug}/preview`}
							className="normal-link">
							Check interface
						</Link>
					</Typography>
					<Typography className="normal-text item-separator-small">
						Students can book available dates -{" "}
						<Link to="/training/availability" className="normal-link">
							Control availability
						</Link>
					</Typography>
				</>
			)}
			{course.status === ECourseStatus.GROUNDED &&
				isFinishedStripe &&
				warnings.length === 0 && (
					<Typography className="normal-text item-separator-small">
						Your service is ready to be published whenever you are! 💪💪
					</Typography>
				)}
			{(!isFinishedStripe || warnings.length > 0) && (
				<div className="publish-warning-container item-separator-small">
					{!isFinishedStripe && (
						<div className="publish-warning-item">
							<div>
								<GoAlert className="normal-icon warning-icon" />
							</div>
							<Typography className="normal-text">
								{"Set up Stripe "}
								<Link to="/stripe" className="normal-text normal-link">
									here
								</Link>
							</Typography>
						</div>
					)}
					{warnings.map((warning, index) => (
						<div className="publish-warning-item" key={`${warning}-${index}`}>
							<div>
								<GoAlert className="normal-icon warning-icon" />
							</div>
							<Typography className="normal-text">{warning}</Typography>
						</div>
					))}
				</div>
			)}
			<div className="item-separator-big">
				{course.status === ECourseStatus.GROUNDED && (
					<Button
						disabled={!(isFinishedStripe && warnings.length === 0)}
						className={classNames("normal-button add-class-button", {
							"black-button": isFinishedStripe && warnings.length === 0,
							"disabled-button": !(isFinishedStripe && warnings.length === 0),
						})}
						onClick={onClickPublishCourse}>
						Publish your Service
					</Button>
				)}
				{course.status === ECourseStatus.FLYING && (
					<Button
						className="normal-button add-class-button black-button"
						onClick={onClickOpenConfirmUnpublishDialog}>
						Unpublish your Service
					</Button>
				)}
			</div>
			<Dialog
				open={confirmUnpublishDialogOpen}
				className="confirm-dialog"
				onClose={onClickCloseConfirmUnpublishDialog}>
				<DialogContent>
					<Typography className="normal-text">
						Are you sure you want to unpublish your service? Students will no
						longer find it in search results.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						className="dialog-button"
						onClick={onClickCloseConfirmUnpublishDialog}>
						Cancel
					</Button>
					<Button className="dialog-button" onClick={onClickUnpublishCourse}>
						Yes, Unpublish
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
